<template>
    <aj-form-detail
        :dialog-title="dialogTitle"
        :dialog-visible.sync="visible"
        :dialog-width="dialogWidth"
        :footer-buttons="footerButtons"
        :show-footer="showFooter"
        :custom-body-style="bodyStyle"
        :modal="false"
        :append-to-body="false"
        :modal-append-to-body="false"
        @close="handleClose"
    >
        <!-- iframe 嵌入 HTML 内容（使用 detail-content 插槽） -->
        <div slot="detail-content" class="iframe-container" :style="containerStyle">
            <iframe
                ref="iframeRef"
                :src="iframeSrc"
                :style="iframeStyle"
                frameborder="0"
                @load="onIframeLoad"
            ></iframe>
        </div>
    </aj-form-detail>
</template>

<script>
import ajFormDetail from '@/components/ajForm/ajFormDetail.vue';

export default {
    name: 'HtmlEmbedDialog',
    components: {
        ajFormDetail
    },
    props: {
        // 弹窗标题
        dialogTitle: {
            type: String,
            default: '页面预览'
        },
        // 弹窗宽度
        dialogWidth: {
            type: [String, Number],
            default: '1200px'
        },
        // HTML 文件路径（相对路径或绝对路径）
        htmlPath: {
            type: String,
            required: true
        },
        // iframe 高度
        iframeHeight: {
            type: [String, Number],
            default: 600
        },
        // 是否显示底部按钮
        showFooter: {
            type: Boolean,
            default: true
        },
        // 底部按钮配置
        footerButtons: {
            type: Array,
            default: () => [
                { text: '关闭', plain: true }
            ]
        },
        // 传递给 iframe 的查询参数对象
        queryParams: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            visible: false,
            iframeSrc: '',
            lastParams: null, // 保存上次的参数，用于状态保持
            initMessageData: null // 保存需要发送的初始化消息数据
        };
    },
    computed: {
        bodyStyle() {
            return {
                padding: '0',
                maxHeight: 'none',
                overflow: 'visible'
            };
        },
        containerStyle() {
            return {
                width: '100%',
                height: typeof this.iframeHeight === 'number' ? `${this.iframeHeight}px` : this.iframeHeight
            };
        },
        iframeStyle() {
            return {
                width: '100%',
                height: '100%',
                border: 'none'
            };
        }
    },
    methods: {
        // 构建带参数的 URL
        buildSrc() {
            console.log('=== buildSrc Debug ===');
            console.log('htmlPath:', this.htmlPath);
            console.log('queryParams:', this.queryParams);
            console.log('queryParams keys:', Object.keys(this.queryParams));

            // 处理基础路径
            let basePath = this.htmlPath;

            // 如果是相对路径，拼接项目基础路径
            if (!basePath.startsWith('http') && !basePath.startsWith('/')) {
                basePath = `/${basePath}`;
            }

            // 构建查询参数
            const params = new URLSearchParams();
            Object.keys(this.queryParams).forEach(key => {
                const value = this.queryParams[key];
                if (value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const queryString = params.toString();
            console.log('queryString:', queryString);
            const result = queryString ? `${basePath}?${queryString}` : basePath;
            console.log('final src:', result);
            return result;
        },

        // 打开弹窗（可选传入参数）
        open(params = null, initMessage = null) {
            console.log('=== HtmlEmbedDialog.open 被调用 ===');
            console.log('传入参数 params:', params);
            console.log('初始化消息 initMessage:', initMessage);
            console.log('当前 lastParams:', this.lastParams);
            console.log('当前 queryParams:', this.queryParams);
            console.log('htmlPath:', this.htmlPath);

            // 保存参数用于状态保持
            if (params) {
                this.lastParams = { ...params };
                console.log('更新 lastParams:', this.lastParams);
            }

            // 保存初始化消息数据
            if (initMessage) {
                this.initMessageData = initMessage;
                console.log('保存 initMessageData:', this.initMessageData);
            }

            // 如果传入了参数，直接使用；否则使用保存的参数或 prop 中的值
            let finalParams = params || this.lastParams || this.queryParams;
            console.log('最终使用的参数 finalParams:', finalParams);

            if (finalParams) {
                this.iframeSrc = this.buildSrcWithParams(finalParams);
            } else {
                this.iframeSrc = this.buildSrc();
            }

            // 添加时间戳参数防止缓存，确保每次打开都加载最新内容
            const timestamp = Date.now();
            this.iframeSrc = this.iframeSrc + (this.iframeSrc.includes('?') ? '&' : '?') + `_t=${timestamp}`;

            console.log('最终 iframeSrc:', this.iframeSrc);

            // 使用 $nextTick 确保 iframe src 设置后再显示弹窗
            this.$nextTick(() => {
                console.log('=== 设置 visible = true ===');
                this.visible = true;
            });
        },

        // 使用指定参数构建 URL
        buildSrcWithParams(customParams) {
            let basePath = this.htmlPath;

            if (!basePath.startsWith('http') && !basePath.startsWith('/')) {
                basePath = `/${basePath}`;
            }

            const params = new URLSearchParams();
            Object.keys(customParams).forEach(key => {
                const value = customParams[key];
                if (value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const queryString = params.toString();
            return queryString ? `${basePath}?${queryString}` : basePath;
        },

        // 关闭弹窗
        close() {
            this.visible = false;
        },

        // 带参数打开弹窗
        openWithParams(params) {
            // 保存参数用于状态保持
            this.lastParams = { ...this.queryParams, ...params };

            this.$emit('update:queryParams', { ...this.queryParams, ...params });
            this.iframeSrc = this.buildSrc();

            // 添加时间戳参数防止缓存
            const timestamp = Date.now();
            this.iframeSrc = this.iframeSrc + (this.iframeSrc.includes('?') ? '&' : '?') + `_t=${timestamp}`;

            // 使用 $nextTick 确保 iframe src 设置后再显示弹窗
            this.$nextTick(() => {
                this.visible = true;
            });
        },

        // 刷新 iframe 内容（不关闭弹窗）
        refresh() {
            const timestamp = Date.now();
            if (this.iframeSrc.includes('_t=')) {
                this.iframeSrc = this.iframeSrc.replace(/_t=\d+/, `_t=${timestamp}`);
            } else {
                this.iframeSrc = this.iframeSrc + (this.iframeSrc.includes('?') ? '&' : '?') + `_t=${timestamp}`;
            }
        },

        // 更新参数并刷新（用于状态保持）
        updateParamsAndRefresh(newParams) {
            this.lastParams = { ...this.lastParams, ...newParams };
            this.$emit('update:queryParams', this.lastParams);

            // 重新构建 src 并添加时间戳
            this.iframeSrc = this.buildSrcWithParams(this.lastParams);
            const timestamp = Date.now();
            this.iframeSrc = this.iframeSrc + (this.iframeSrc.includes('?') ? '&' : '?') + `_t=${timestamp}`;
        },

        // iframe 加载完成
        onIframeLoad() {
            console.log('=== iframe 加载完成 ===');
            console.log('iframeSrc:', this.iframeSrc);

            // 优先发送 initMessageData（外部传入的初始化消息）
            if (this.initMessageData) {
                console.log('=== 发送 initMessageData 给 iframe ===');
                console.log('initMessageData:', this.initMessageData);
                this.postMessage(this.initMessageData);
                this.initMessageData = null; // 发送后清空
            }
            // 其次发送 lastParams 作为初始化消息
            else if (this.lastParams) {
                console.log('=== 发送 lastParams 作为初始化消息 ===');
                console.log('lastParams:', this.lastParams);
                this.postMessage({
                    type: 'init',
                    payload: this.lastParams
                });
            }

            this.$emit('iframe-load');
        },

        // 处理关闭
        handleClose() {
            this.$emit('close');
        },

        // 向 iframe 发送消息
        postMessage(data, targetOrigin = '*') {
            const iframe = this.$refs.iframeRef;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(data, targetOrigin);
            }
        },

        // 监听来自 iframe 的消息
        initMessageListener() {
            window.addEventListener('message', this.handleMessage);
        },

        // 处理来自 iframe 的消息
        handleMessage(event) {
            this.$emit('message', event.data, event);
        },

        // 移除消息监听
        removeMessageListener() {
            window.removeEventListener('message', this.handleMessage);
        }
    },
    mounted() {
        this.initMessageListener();
    },
    beforeDestroy() {
        this.removeMessageListener();
    }
};
</script>

<style scoped>
.iframe-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #fff;
    padding: 0;
}

.iframe-container iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
}
</style>
